import { useQuery, useMutation } from '@tanstack/react-query';
import { CreditCard, Sparkles } from 'lucide-react';
import { api } from '../api.js';
import { Card, CardHeader, Button, Badge, PageHeader, Spinner } from '../components/ui.jsx';

const BASE_AUD = 2500;
const PER_USER_AUD = 50;

export default function BillingPage() {
  const { data, isLoading } = useQuery({ queryKey: ['billing'], queryFn: api.getBilling });

  const checkout = useMutation({
    mutationFn: api.createCheckoutSession,
    onSuccess: (res) => { if (res.url) window.location.href = res.url; }
  });
  const portal = useMutation({
    mutationFn: api.createPortalSession,
    onSuccess: (res) => { if (res.url) window.location.href = res.url; }
  });

  if (isLoading || !data) return <div className="min-h-[40vh] grid place-items-center"><Spinner className="w-8 h-8 text-primary-600" /></div>;

  const annual = BASE_AUD + (data.licensed_users || 0) * PER_USER_AUD;
  const isActive = data.subscription_status === 'active';

  return (
    <>
      <PageHeader title="Billing" description="One price, all 12 modules, every feature. AUD billed annually + GST." />

      <Card className="mb-6 bg-gradient-to-br from-primary-50/40 to-accent-50/30">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary-700 to-accent-600 grid place-items-center text-white"><Sparkles className="w-5 h-5" /></div>
          <div className="flex-1">
            <Badge variant={isActive ? 'green' : data.subscription_status === 'trialing' ? 'amber' : data.subscription_status === 'past_due' ? 'red' : 'grey'}>
              {data.subscription_status?.replace('_', ' ')}
            </Badge>
            {data.trial_ends_at && data.subscription_status === 'trialing' ? (
              <p className="text-sm text-ink-700 mt-2">Your trial ends <strong>{new Date(data.trial_ends_at).toLocaleDateString('en-AU')}</strong>. Activate your subscription to keep access.</p>
            ) : null}
          </div>
        </div>
      </Card>

      <div className="grid md:grid-cols-2 gap-6 mb-6">
        <Card>
          <CardHeader title="Plan" />
          <div className="space-y-3">
            <Row label="Base licence" value="A$2,500 + GST" sub="per company per year" />
            <Row label="Per user" value="A$50 + GST" sub="per active user per year" />
            <div className="border-t border-ink-200 pt-3">
              <Row label="Current annual" value={`A$${annual.toLocaleString()} + GST`} sub={`${data.licensed_users || 0} active user${data.licensed_users === 1 ? '' : 's'}`} bold />
            </div>
          </div>
        </Card>

        <Card>
          <CardHeader title="What's included" />
          <ul className="space-y-2 text-sm text-ink-700">
            <li>• All 12 assessment modules</li>
            <li>• Unlimited reassessment cycles</li>
            <li>• AI-generated personal reports + team insights</li>
            <li>• SSO / SAML 2.0</li>
            <li>• Australian data residency</li>
            <li>• Audit logging + DPA available on request</li>
            <li>• Email support · Priority for super-users</li>
          </ul>
        </Card>
      </div>

      <Card>
        <CardHeader title="Manage subscription" />
        {!isActive ? (
          <div>
            <p className="text-sm text-ink-600 mb-4">Activate your subscription to keep your workspace running beyond the trial. Stripe handles payment securely with GST applied automatically.</p>
            <Button onClick={() => checkout.mutate()} loading={checkout.isPending} size="lg">
              <CreditCard className="w-4 h-4 mr-1.5" />Activate subscription
            </Button>
          </div>
        ) : (
          <div>
            <p className="text-sm text-ink-600 mb-4">Update payment method, change billing address, or download invoices in the Stripe billing portal.</p>
            <Button variant="secondary" onClick={() => portal.mutate()} loading={portal.isPending}>Open billing portal</Button>
          </div>
        )}
      </Card>
    </>
  );
}

function Row({ label, value, sub, bold }) {
  return (
    <div className="flex items-baseline justify-between">
      <div>
        <div className={'text-sm ' + (bold ? 'font-semibold text-ink-900' : 'text-ink-700')}>{label}</div>
        {sub ? <div className="text-xs text-ink-500">{sub}</div> : null}
      </div>
      <div className={bold ? 'text-base font-semibold text-ink-900' : 'text-sm text-ink-700'}>{value}</div>
    </div>
  );
}
